import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { Plus, ShoppingCart, X, Trash2, TrendingUp, TrendingDown, Minus, Search, CreditCard, Clock, Smartphone, Users, DollarSign, Receipt, FileText } from 'lucide-react';
import AmountInput from '../components/AmountInput';

const Sales = () => {
  const { t } = useTranslation();
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [items, setItems] = useState([{ productId: '', quantity: '', unitPrice: '' }]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saleType, setSaleType] = useState('cash');
  const [saleInfo, setSaleInfo] = useState({
    customerName: '', customerPhone: '', dueDate: '', installmentMonths: '3',
    paidAmount: '', subscriptionPeriod: 'monthly', digitalPlatform: '',
    transactionReference: '', notes: ''
  });

  const saleTypes = [
    { value: 'cash', label: 'Cash Sale', icon: DollarSign, desc: 'Full payment at time of sale' },
    { value: 'credit', label: 'Credit Sale', icon: Clock, desc: 'Pay later with due date' },
    { value: 'installment', label: 'Installment', icon: CreditCard, desc: 'Pay in monthly installments' },
    { value: 'bulk', label: 'Bulk Sale', icon: ShoppingCart, desc: 'Large quantity to wholesaler' },
    { value: 'subscription', label: 'Subscription', icon: Receipt, desc: 'Recurring subscription' },
    { value: 'digital', label: 'Digital Sale', icon: Smartphone, desc: 'Online/Mobile sale' },
    { value: 'financial_service', label: 'Financial Service', icon: Users, desc: 'Loans, insurance, etc.' },
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [salesRes, prodRes] = await Promise.all([api.get('/sales'), api.get('/products/with-stock')]);
      setSales(Array.isArray(salesRes.data) ? salesRes.data : []);
      setProducts(prodRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const addItem = () => setItems([...items, { productId: '', quantity: '', unitPrice: '' }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => {
    const newItems = [...items];
    newItems[i][field] = value;
    setItems(newItems);
  };

  const handleProductSelect = (i, productId) => {
    const product = products.find(p => p.id.toString() === productId);
    if (product) {
      updateItem(i, 'productId', productId);
      updateItem(i, 'unitPrice', product.sellingPrice?.toString() || '');
    } else {
      updateItem(i, 'productId', productId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formatted = items.map(i => ({
      productId: Number(i.productId),
      quantity: Number(i.quantity),
      unitPrice: Number(i.unitPrice)
    }));
    try {
      const payload = {
        items: formatted,
        saleType: saleType,
        ...saleInfo,
        ...(saleInfo.installmentMonths && { installmentMonths: Number(saleInfo.installmentMonths) }),
        ...(saleInfo.paidAmount && { paidAmount: Number(saleInfo.paidAmount.replace(/[^0-9]/g, '')) })
      };
      await api.post('/sales', payload);
      setShowModal(false);
      setItems([{ productId: '', quantity: '', unitPrice: '' }]);
      setSaleType('cash');
      setSaleInfo({ customerName: '', customerPhone: '', dueDate: '', installmentMonths: '3', paidAmount: '', subscriptionPeriod: 'monthly', digitalPlatform: '', transactionReference: '', notes: '' });
      fetchData();
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale? Stock will be restored.')) return;
    try { await api.delete(`/sales/${id}`); fetchData(); }
    catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleRecordPayment = async (saleId) => {
    const amount = prompt('Enter payment amount (TZS):');
    if (!amount) return;
    try { await api.put(`/sales/${saleId}/payment`, { amount: Number(amount) }); fetchData(); }
    catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const downloadInvoice = async (saleId) => {
    try {
      const response = await api.get(`/invoices/sale/${saleId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-INV-${saleId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert('Failed to download invoice'); }
  };

  const formatCurrency = (v) => 'TZS ' + Number(v || 0).toLocaleString('en');

  const getPaymentBadge = (status) => {
    if (status === 'paid') return 'bg-emerald-500/20 text-emerald-400';
    if (status === 'pending') return 'bg-yellow-500/20 text-yellow-400';
    if (status === 'partial') return 'bg-blue-500/20 text-blue-400';
    if (status === 'overdue') return 'bg-red-500/20 text-red-400';
    return 'bg-gray-500/20 text-gray-400';
  };

  const getSaleTypeIcon = (type) => {
    const st = saleTypes.find(s => s.value === type);
    return st ? st.icon : ShoppingCart;
  };

  const filteredSales = sales.filter(s => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (s.saleDate?.includes(term) || s.customerName?.toLowerCase().includes(term) || s.saleType?.toLowerCase().includes(term));
  });

  const totalProfit = sales.reduce((sum, s) => sum + (Number(s.profit) || 0), 0);
  const totalRevenue = sales.reduce((sum, s) => sum + (Number(s.totalAmount) || 0), 0);
  const totalPending = sales.filter(s => s.paymentStatus === 'pending' || s.paymentStatus === 'partial').reduce((sum, s) => sum + (Number(s.dueAmount) || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h2 className="text-xl md:text-2xl font-bold text-white">{t('sales')}</h2><p className="text-gray-400 text-sm mt-1">Manage all types of sales & payments</p></div>
        <button onClick={() => { setSaleType('cash'); setSaleInfo({ customerName: '', customerPhone: '', dueDate: '', installmentMonths: '3', paidAmount: '', subscriptionPeriod: 'monthly', digitalPlatform: '', transactionReference: '', notes: '' }); setItems([{ productId: '', quantity: '', unitPrice: '' }]); setShowModal(true); }}
          className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-emerald-600 transition text-sm shadow-lg shadow-emerald-500/20"><Plus size={18} /> New Sale</button>
      </div>

      {sales.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-[#0d1410] border border-white/5 rounded-xl p-3"><p className="text-gray-500 text-xs">Total Sales</p><p className="text-white font-bold text-lg">{sales.length}</p></div>
          <div className="bg-[#0d1410] border border-white/5 rounded-xl p-3"><p className="text-gray-500 text-xs">Revenue</p><p className="text-white font-bold text-lg">{formatCurrency(totalRevenue)}</p></div>
          <div className="bg-[#0d1410] border border-white/5 rounded-xl p-3"><p className="text-gray-500 text-xs">Total Profit</p><p className={`font-bold text-lg ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(totalProfit)}</p></div>
          <div className="bg-[#0d1410] border border-white/5 rounded-xl p-3"><p className="text-gray-500 text-xs">Pending/Due</p><p className="text-yellow-400 font-bold text-lg">{formatCurrency(totalPending)}</p></div>
        </div>
      )}

      {sales.length > 0 && (
        <div className="relative"><Search size={16} className="absolute left-3 top-2.5 text-gray-500" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by date, customer, sale type..."
            className="w-full pl-10 pr-4 py-2.5 bg-[#0d1410] border border-white/5 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" />
        </div>
      )}

      {filteredSales.length === 0 ? (
        <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-12 text-center"><ShoppingCart size={48} className="text-gray-600 mx-auto mb-4" /><p className="text-gray-400 font-medium">{searchTerm ? 'No matching sales' : 'No sales yet'}</p></div>
      ) : (
        <div className="space-y-3">
          {filteredSales.map((sale) => {
            const SaleTypeIcon = getSaleTypeIcon(sale.saleType);
            return (
              <div key={sale.id} className="bg-[#0d1410] border border-white/5 rounded-xl hover:border-white/10 transition-all overflow-hidden">
                <div className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-[#0a0f0d] rounded-xl"><SaleTypeIcon size={20} className="text-gray-300" /></div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium text-sm capitalize">{sale.saleType?.replace('_', ' ') || 'cash'} Sale</p>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getPaymentBadge(sale.paymentStatus)}`}>{sale.paymentStatus}</span>
                        </div>
                        <p className="text-gray-500 text-xs">{sale.saleDate} {sale.customerName && `• ${sale.customerName}`}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-white font-bold text-sm">{formatCurrency(sale.totalAmount)}</p>
                        {sale.dueAmount > 0 && <p className="text-yellow-400 text-xs">Due: {formatCurrency(sale.dueAmount)}</p>}
                        <p className={`text-xs font-medium ${sale.status === 'PROFIT' ? 'text-emerald-400' : sale.status === 'LOSS' ? 'text-red-400' : 'text-gray-400'}`}>
                          {sale.status === 'PROFIT' ? '+' : sale.status === 'LOSS' ? '-' : ''}{formatCurrency(Math.abs(sale.profit || 0))}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {(sale.paymentStatus === 'pending' || sale.paymentStatus === 'partial') && (
                          <button onClick={() => handleRecordPayment(sale.id)} className="p-1.5 text-gray-500 hover:text-emerald-400 transition"><DollarSign size={14} /></button>
                        )}
                        <button onClick={() => downloadInvoice(sale.id)} className="p-1.5 text-gray-500 hover:text-blue-400 transition"><FileText size={14} /></button>
                        <button onClick={() => handleDelete(sale.id)} className="p-1.5 text-gray-500 hover:text-red-400 transition"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                  {sale.items && (
                    <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-2">
                      {sale.items.map((item, i) => (
                        <span key={i} className="text-xs bg-white/5 rounded-lg px-2 py-1 text-gray-400">{item.productName} x{item.quantity}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111a14] border border-white/10 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6"><h3 className="text-white font-bold text-lg">New Sale</h3><button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X size={20} /></button></div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Sale Type</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {saleTypes.map(st => (
                    <button key={st.value} type="button" onClick={() => setSaleType(st.value)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-xs transition-all ${saleType === st.value ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-[#0d1611] border-white/5 text-gray-400 hover:border-white/10'}`}>
                      <st.icon size={18} /><span className="font-medium">{st.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {(saleType === 'credit' || saleType === 'installment' || saleType === 'bulk' || saleType === 'subscription' || saleType === 'financial_service') && (
                <div className="bg-[#0d1611] border border-white/5 rounded-xl p-4 space-y-3">
                  <p className="text-gray-400 text-xs font-medium uppercase">{saleType.replace('_', ' ')} Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" placeholder="Customer Name" value={saleInfo.customerName} onChange={e => setSaleInfo({...saleInfo, customerName: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                    <input type="text" placeholder="Customer Phone" value={saleInfo.customerPhone} onChange={e => setSaleInfo({...saleInfo, customerPhone: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                    {saleType === 'credit' && <input type="date" value={saleInfo.dueDate} onChange={e => setSaleInfo({...saleInfo, dueDate: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />}
                    {saleType === 'installment' && (
                      <>
                        <input type="number" placeholder="Number of Months" value={saleInfo.installmentMonths} onChange={e => setSaleInfo({...saleInfo, installmentMonths: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                        <AmountInput placeholder="Down Payment (TZS)" value={saleInfo.paidAmount} onChange={e => setSaleInfo({...saleInfo, paidAmount: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                      </>
                    )}
                    {saleType === 'digital' && (
                      <>
                        <input type="text" placeholder="Platform (e.g. M-Pesa)" value={saleInfo.digitalPlatform} onChange={e => setSaleInfo({...saleInfo, digitalPlatform: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                        <input type="text" placeholder="Transaction Reference" value={saleInfo.transactionReference} onChange={e => setSaleInfo({...saleInfo, transactionReference: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
                      </>
                    )}
                  </div>
                </div>
              )}
              {items.map((item, i) => (
                <div key={i} className="bg-[#0d1611] border border-white/5 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between"><span className="text-gray-400 text-xs font-medium">Item {i + 1}</span>{items.length > 1 && <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>}</div>
                  <select value={item.productId} onChange={e => handleProductSelect(i, e.target.value)} className="w-full px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" required>
                    <option value="">Select product</option>
                    {products.map(p => <option key={p.id} value={p.id} className="bg-[#0a0f0d]">{p.name} (Stock: {p.currentQuantity || 0})</option>)}
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" placeholder="Quantity" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" required />
                    <AmountInput placeholder="Selling Price (TZS)" value={item.unitPrice} onChange={e => updateItem(i, 'unitPrice', e.target.value)} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" required />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addItem} className="w-full py-2.5 border border-dashed border-white/10 rounded-xl text-gray-400 hover:text-white text-sm flex items-center justify-center gap-2"><Plus size={16} /> Add Item</button>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 border border-white/10 rounded-xl text-gray-400 hover:text-white transition text-sm">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition text-sm">Record Sale</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;
